import { useState } from "react";
import { Gamepad2, Code, Palette, Map, Sparkles } from "lucide-react";

interface GameShowcaseProps {
  title: string;
  tagline: string;
  genre: string[];
  progress: number;
  dimension?: "2D" | "3D";
}

const GameShowcase = ({ title, tagline, genre, progress, dimension = "2D" }: GameShowcaseProps) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className="cyber-card p-10 bg-gradient-to-br from-background via-primary/5 to-accent/5 relative overflow-hidden group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Animated icons in background */}
      <div className={`absolute inset-0 transition-opacity duration-700 ${isHovered ? "opacity-20" : "opacity-0"}`}>
        <Code className="absolute top-10 left-10 w-16 h-16 text-primary animate-pulse" style={{ animationDelay: "0s" }} />
        <Palette className="absolute top-20 right-10 w-12 h-12 text-accent animate-pulse" style={{ animationDelay: "0.5s" }} />
        <Map className="absolute bottom-20 left-20 w-14 h-14 text-secondary animate-pulse" style={{ animationDelay: "1s" }} />
        <Sparkles className="absolute bottom-10 right-20 w-10 h-10 text-primary animate-pulse" style={{ animationDelay: "1.5s" }} />
      </div>

      {/* Content */}
      <div className="relative z-10 space-y-8">
        {/* Header */}
        <div className="text-center space-y-4">
          <div className="flex justify-center">
            <div className={`p-6 rounded-sm border border-accent bg-accent/10 transition-all duration-500 ${
              isHovered ? "scale-110 rotate-12" : ""
            }`}>
              <Gamepad2 className="w-20 h-20 text-accent" />
            </div>
          </div>

          <div className="flex items-center justify-center gap-4 mb-4">
            <h2 className="text-4xl md:text-5xl font-cyber font-bold">
              <span className="text-primary">{'<'}</span>
              <span className="terminal-text">{title}</span>
              <span className="text-primary">{'/>'}</span>
            </h2>
            <span className={`px-4 py-2 text-base font-mono border-2 rounded-sm font-bold ${
              dimension === "2D" ? "border-secondary/80 text-secondary bg-secondary/10 neon-glow-purple" :
              "border-accent/80 text-accent bg-accent/10 neon-glow-pink"
            }`}>
              {dimension}
            </span>
          </div>

          <p className="text-xl font-mono text-accent">{tagline}</p>
        </div>

        {/* Genre tags */}
        <div className="flex flex-wrap justify-center gap-3">
          {genre.map((g, i) => (
            <span
              key={i}
              className={`text-sm font-mono px-4 py-2 border border-primary/50 text-primary bg-primary/5 transition-all duration-300 ${
                isHovered ? "scale-110 border-primary" : ""
              }`}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              {g}
            </span>
          ))}
        </div>

        {/* Progress bar */}
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <p className="text-sm font-mono text-muted-foreground">
              <span className="text-accent">{'>> '}</span>ПРОГРЕСС РАЗРАБОТКИ
            </p>
            <p className="text-2xl font-cyber font-bold text-primary">{progress}%</p>
          </div>
          
          <div className="h-4 bg-background/50 border border-primary/30 overflow-hidden relative">
            <div
              className="h-full bg-gradient-to-r from-primary via-accent to-secondary relative overflow-hidden transition-all duration-1000"
              style={{ width: `${progress}%` }}
            >
              <div className="absolute inset-0 bg-white/20 animate-pulse" />
            </div>
            
            {/* Segments */}
            <div className="absolute inset-0 flex">
              {Array.from({ length: 10 }).map((_, i) => (
                <div key={i} className="flex-1 border-r border-background/50 last:border-r-0" />
              ))}
            </div>
          </div>
        </div>

        {/* Status indicator */}
        <div className="flex items-center justify-center gap-3 py-4">
          <div className="w-3 h-3 rounded-full bg-accent animate-pulse shadow-lg shadow-accent/50" />
          <span className="font-mono text-sm text-accent font-bold">В АКТИВНОЙ РАЗРАБОТКЕ</span>
          <div className="w-3 h-3 rounded-full bg-accent animate-pulse shadow-lg shadow-accent/50" />
        </div>
      </div>

      {/* Decorative corners */}
      <div className="absolute top-0 left-0 w-20 h-20 border-l-2 border-t-2 border-primary/30 transition-all duration-500 group-hover:border-primary" />
      <div className="absolute top-0 right-0 w-20 h-20 border-r-2 border-t-2 border-accent/30 transition-all duration-500 group-hover:border-accent" />
      <div className="absolute bottom-0 left-0 w-20 h-20 border-l-2 border-b-2 border-secondary/30 transition-all duration-500 group-hover:border-secondary" />
      <div className="absolute bottom-0 right-0 w-20 h-20 border-r-2 border-b-2 border-primary/30 transition-all duration-500 group-hover:border-primary" />
    </div>
  );
};

export default GameShowcase;
